/**
 * Adicionar amigo e responder solicitacoes, no topo da aba de conversas.
 *
 * O campo de `@handle` e o UNICO jeito de achar alguem novo no LFRCORD. Isso
 * e de proposito: e o que permite adicionar quem voce conhece sem que exista
 * em lugar nenhum uma lista de todas as pessoas cadastradas para vasculhar.
 */

import { useState, type CSSProperties, type FormEvent } from "react";
import { handleError, normalizeHandle } from "@shared/protocol";
import { pendingFriendRequests, sentFriendRequests, useApp } from "@/state/store";
import { C, FONT_MONO, FONT_SANS, hexA, initialOf } from "@/theme";

const SECTION_LABEL: CSSProperties = {
  fontFamily: FONT_MONO,
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: ".14em",
  color: C.textLabelDim,
};

function Avatar({
  name,
  avatarUrl,
  color,
}: {
  name: string;
  avatarUrl: string | null;
  color: string | null;
}) {
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt=""
        style={{ width: 28, height: 28, borderRadius: 999, objectFit: "cover", flex: "0 0 auto" }}
      />
    );
  }
  return (
    <div
      style={{
        width: 28,
        height: 28,
        flex: "0 0 auto",
        display: "grid",
        placeItems: "center",
        borderRadius: 999,
        background: color ?? "var(--ac, #1F4FD8)",
        color: C.ink,
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      {initialOf(name)}
    </div>
  );
}

export function FriendsPanel() {
  const { state, actions } = useApp();
  const [handle, setHandle] = useState("");
  const [open, setOpen] = useState(false);

  const received = pendingFriendRequests(state);
  const sent = sentFriendRequests(state);

  // O erro de formato aparece enquanto digita; o de "essa pessoa não existe"
  // vem do servidor pelo aviso geral do app (`state.notice`).
  const problem = handle.length === 0 ? null : handleError(handle);

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (problem !== null || handle.trim().length === 0) return;
    actions.addFriend({ handle: normalizeHandle(handle) });
    setHandle("");
  }

  if (!state.friendsAvailable) {
    return (
      <div
        style={{
          margin: "10px 8px",
          padding: "9px 11px",
          border: `1px solid ${hexA(C.amber, 0.28)}`,
          borderRadius: 9,
          background: hexA(C.amber, 0.08),
          fontSize: 11.5,
          lineHeight: 1.5,
          color: C.textMuted,
        }}
      >
        As amizades ainda não estão ligadas neste servidor: falta aplicar a migração{" "}
        <span style={{ fontFamily: FONT_MONO }}>sql/2026-08-24-amizades.sql</span>. Até lá, a lista
        abaixo mostra os membros da comunidade, como antes.
      </div>
    );
  }

  return (
    <div style={{ margin: "10px 8px 4px", display: "grid", gap: 8 }}>
      {/* Adicionar */}
      {open ? (
        <form onSubmit={submit} style={{ display: "grid", gap: 6 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "7px 10px",
              border: `1px solid ${problem ? hexA(C.red, 0.5) : C.lineStrong}`,
              borderRadius: 9,
              background: C.field,
            }}
          >
            <span style={{ fontFamily: FONT_MONO, fontSize: 12, color: C.textGhost }}>@</span>
            <input
              value={handle}
              onChange={event => setHandle(normalizeHandle(event.target.value))}
              placeholder="nome de usuário exato"
              autoFocus
              spellCheck={false}
              maxLength={24}
              style={{
                flex: 1,
                minWidth: 0,
                border: 0,
                background: "transparent",
                color: C.text,
                fontFamily: FONT_MONO,
                fontSize: 12.5,
                outline: 0,
              }}
            />
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              type="submit"
              disabled={problem !== null || handle.length === 0}
              className="hov-bright-108 act-press"
              style={{
                flex: 1,
                padding: "7px 0",
                border: 0,
                borderRadius: 8,
                cursor: problem !== null || handle.length === 0 ? "default" : "pointer",
                background: "var(--ac, #1F4FD8)",
                color: C.ink,
                fontFamily: FONT_SANS,
                fontSize: 12,
                fontWeight: 700,
                opacity: problem !== null || handle.length === 0 ? 0.55 : 1,
              }}
            >
              Enviar
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setHandle("");
              }}
              style={{
                padding: "7px 12px",
                border: 0,
                borderRadius: 8,
                cursor: "pointer",
                background: "transparent",
                color: C.textMuted,
                fontSize: 12,
              }}
            >
              Cancelar
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="hov-border"
          style={{
            padding: "8px 0",
            border: `1px dashed ${C.lineStrong}`,
            borderRadius: 9,
            cursor: "pointer",
            background: "transparent",
            color: C.textMuted,
            fontFamily: FONT_SANS,
            fontSize: 12.5,
            fontWeight: 600,
          }}
        >
          + Adicionar amigo
        </button>
      )}

      {/* Solicitações recebidas */}
      {received.length > 0 ? (
        <div style={{ display: "grid", gap: 4 }}>
          <div style={{ ...SECTION_LABEL, marginTop: 4 }}>
            SOLICITAÇÕES · {received.length}
          </div>
          {received.map(link => {
            const profile = state.friendProfiles[link.userId];
            if (!profile) return null;
            return (
              <div
                key={link.userId}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 9,
                  padding: "6px 8px",
                  borderRadius: 8,
                  background: hexA(C.accent, 0.08),
                }}
              >
                <Avatar name={profile.name} avatarUrl={profile.avatarUrl} color={profile.color} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontSize: 12.5,
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {profile.name}
                  </div>
                  <div style={{ fontFamily: FONT_MONO, fontSize: 10, color: C.textGhost }}>
                    @{profile.handle}
                  </div>
                </div>
                <button
                  type="button"
                  title="Aceitar"
                  onClick={() => actions.acceptFriend(link.userId)}
                  className="act-press"
                  style={{
                    padding: "5px 10px",
                    border: 0,
                    borderRadius: 7,
                    cursor: "pointer",
                    background: "var(--ac, #1F4FD8)",
                    color: C.ink,
                    fontSize: 11.5,
                    fontWeight: 700,
                  }}
                >
                  Aceitar
                </button>
                <button
                  type="button"
                  title="Recusar"
                  onClick={() => actions.removeFriend(link.userId)}
                  className="act-press"
                  style={{
                    padding: "5px 9px",
                    border: `1px solid ${C.lineStrong}`,
                    borderRadius: 7,
                    cursor: "pointer",
                    background: "transparent",
                    color: C.textMuted,
                    fontSize: 11.5,
                  }}
                >
                  Não
                </button>
              </div>
            );
          })}
        </div>
      ) : null}

      {/*
        Enviadas: mostra PARA QUEM, não só quantas.
        Antes era só o número, e isso confundia de verdade: quem tinha mandado
        pedido para duas pessoas parecidas via "1 solicitação enviada" continuar
        na tela depois de uma delas aceitar, e concluía que o aceite não tinha
        funcionado — quando na verdade sobrava a outra, ainda pendente.
      */}
      {sent.length > 0 ? (
        <div style={{ display: "grid", gap: 4 }}>
          <div style={{ ...SECTION_LABEL, marginTop: 4 }}>ESPERANDO RESPOSTA · {sent.length}</div>
          {sent.map(link => {
            const profile = state.friendProfiles[link.userId];
            if (!profile) return null;
            return (
              <div
                key={link.userId}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 9,
                  padding: "6px 8px",
                  borderRadius: 8,
                }}
              >
                <Avatar name={profile.name} avatarUrl={profile.avatarUrl} color={profile.color} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontSize: 12.5,
                      color: C.textMuted,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {profile.name}
                  </div>
                  <div style={{ fontFamily: FONT_MONO, fontSize: 10, color: C.textGhost }}>
                    @{profile.handle}
                  </div>
                </div>
                <button
                  type="button"
                  title="Cancelar a solicitação"
                  onClick={() => actions.removeFriend(link.userId)}
                  className="act-press"
                  style={{
                    padding: "5px 9px",
                    border: `1px solid ${C.lineStrong}`,
                    borderRadius: 7,
                    cursor: "pointer",
                    background: "transparent",
                    color: C.textMuted,
                    fontSize: 11.5,
                  }}
                >
                  Cancelar
                </button>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
